/**
 * The virtual account page (TMMAPP-0005, rebuilt to prototype v2 by TMMAPP-0108; audit 06 § A1–A11,
 * § Overrides 1–8; v2 L178–683) at `/v/:virtualAccountId`.
 *
 * - Header (A1): the "Money Tools" eyebrow (`PageHeader`, TMMAPP-0150 U2), the `h1` with the page kebab, the share line —
 *   "Shared with A, B" / "Private — not shared with anyone" / "Shared with you by X" — and, for a
 *   viewer who can't write, the READ-ONLY chip. No header actions: Sharing is in the menu.
 * - D1 banners (trial, share failure, archived) between the header and the balance card.
 * - The balance card with the envelopes well (`BalanceCard`), above the tabs.
 * - Tabs: for the owner (a subscriber), **Items to review** first, the suggested envelopes from the
 *   imported history (TMMAPP-0141 §B; TMMAPP-0159 §F 30); then Ledger (the main tab) · Categories ·
 *   Cashflow forecast · Meetings & summaries, then D1 Items · Activity; the tab is in the URL
 *   (`?tab=`, absent = the default by the review count), and a tab switch clears the tab being
 *   left's params.
 *
 * Roles (TMMAPP-0110, MM-D-115 / 116): every control reads the served standing through
 * `useVirtualAccountRole()`. Writers are the served `canManage` (the owner or a Manager); a Viewer —
 * and everyone while the owner is in GRACE — reads, with the READ-ONLY chip; a LOCKED owner's virtual
 * account shows the locked state. Owner-only stays the owner's: Sharing, archive / restore, envelope
 * move and AI. Money renders in the OWNER's currency
 * (`detail.currency`); every figure is served. A 404 renders the not-found page — never
 * distinguishing absent from unshared (MM-D-05).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  Paper,
  Skeleton,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sponsorName } from '@/features/onboarding/sponsorName';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { AddNewCard } from '@/components/AddNewCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { type ReorderHandleProps } from '@/components/SortableCardGrid';
import { ActivityTab } from './ActivityTab';
import { VirtualAccountItemsTab } from './VirtualAccountItemsTab';
import { ErrorCodes, isApiError, isNotFound, standingRefusal } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import type { MeVirtualAccount } from '@/api/me';
import { envelopesApi, type Envelope } from '@/api/envelopes';
import { RenameVirtualAccountDialog } from '@/features/virtualAccounts/RenameVirtualAccountDialog';
import { useVirtualAccountsList } from '@/features/virtualAccounts/virtualAccountsListContext';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { SuggestedEnvelopes } from '@/features/genesis/SuggestedEnvelopes';
import { useGenesisProposals } from '@/features/genesis/useGenesisProposals';
import { ReviewPanel, ReviewTabLoading, ReviewTabs } from '@/components/ReviewTabs';
import { useReviewTab, type ReviewCount, type ReviewTabValue } from '@/components/reviewTab';
import { MoneyText } from '@/components/MoneyText';
import { AskAboutThis } from '@/features/assistant/AskAboutThis';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { NotFoundBody, RecordNotFound } from '@/routes/NotFound';
import { useMe, useMeRefetch } from '@/session/meHooks';
import { useSubscriberAction } from '@/session/useSubscriberAction';
import { useVirtualAccountRole } from '@/session/useVirtualAccountRole';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { useNotify } from '@/shell/NotifyProvider';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { downloadBlob } from '@/utils/downloadBlob';
import { slug } from '@/utils/slug';
import { AnalysisDialog } from '@/features/ai/AnalysisDialog';
import { AlertsDialog } from '@/features/envelope/alerts/AlertsDialog';
import { canSeeAlerts, canWriteAlerts } from '@/features/envelope/alerts/canSeeAlerts';
import {
  enumParam,
  intParam,
  optionalEnumParam,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import { destructiveItemSx } from '@/shell/objectMenu';
import { resolveTokens } from '@/theme/theme';
import { ArchivedEnvelopesDialog } from './ArchivedEnvelopesDialog';
import { BalanceCard } from './BalanceCard';
import { CategoriesTab } from './CategoriesTab';
import { DownloadRangeDialog, type DownloadKind } from './DownloadRangeDialog';
import { EnvelopeActionsMenu, type EnvelopeCardActions } from './EnvelopeCard';
import { EnvelopeTile } from './EnvelopeTile';
import { envelopeTileContent } from './envelopeTileContent';
import { audienceFromDetail, useStandingAgreement, visibilityLine } from './audience';
import { EnvelopeFormDialog } from './EnvelopeFormDialog';
import { InfoDialog } from './InfoDialog';
import { LedgerTab } from './LedgerTab';
import { LEDGER_FILTERS, type LedgerFilter } from './ledgerUrl';
import { MeetingsSummariesTab } from './MeetingsSummariesTab';
import { MEETING_TABS, type MeetingTab } from './meetingsUrl';
import { MoveEnvelopeDialog } from './MoveEnvelopeDialog';
import { MoveMoneyDialog } from './MoveMoneyDialog';
import { GraceNotice, VirtualAccountLockedBody } from './OwnerAccessNotice';
import { SharingDialog } from './SharingDialog';
import { SplitRulesDialog } from './SplitRulesDialog';
import { SplitUnassignedDialog } from './SplitUnassignedDialog';
import { UnassignedRegisterDialog } from './UnassignedRegisterDialog';
import { VirtualAccountForecastTab } from './VirtualAccountForecastTab';
import { parseRouteId } from './routeId';
import { nextSortOrder, orderEnvelopes, withActiveSortOrder } from './envelopeOrder';

const VIRTUAL_ACCOUNT_TABS = [
  'ledger',
  'categories',
  'forecast',
  'meetings',
  'items',
  'activity',
] as const;
type VirtualAccountTab = (typeof VIRTUAL_ACCOUNT_TABS)[number];

// Every tab's own params live in one query string: the ledger's search and filter, the forecast's
// envelope filter and horizon, the meetings sub-tab, Items' kind / status and the change log's range,
// kind and person.
const URL_SCHEMA = {
  // Absent: the default by the Items to review count (TMMAPP-0159 §F 27); a chosen tab is kept.
  tab: optionalEnumParam<VirtualAccountTab | ReviewTabValue>([...VIRTUAL_ACCOUNT_TABS, 'review']),
  q: stringParam(),
  filter: enumParam<LedgerFilter>(LEDGER_FILTERS, 'all'),
  envelope: intParam(0, { min: 0 }),
  h: stringParam(),
  mt: enumParam<MeetingTab>(MEETING_TABS, 'upcoming'),
  af: stringParam(),
  at: stringParam(),
  ak: stringParam(),
  ap: intParam(0, { min: 0 }),
  kind: stringParam(),
  status: stringParam(),
  // Categories: the Totals | P&L view and the P&L's month range (TMMAPP-0113 4e).
  view: stringParam(),
  from: stringParam(),
  to: stringParam(),
};

/**
 * The params each tab owns, as the patch that clears them. A tab switch clears the tab being left,
 * so Items' `kind` / `status` never follow you to the Ledger (TMMAPP-0081, NAV-009).
 */
type UrlPatch = Partial<{ [K in keyof typeof URL_SCHEMA]: (typeof URL_SCHEMA)[K]['default'] }>;
const TAB_PARAMS: Record<VirtualAccountTab | ReviewTabValue, UrlPatch> = {
  review: {},
  ledger: { q: '', filter: 'all' },
  categories: { view: '', from: '', to: '' },
  forecast: { envelope: 0, h: '' },
  meetings: { mt: 'upcoming' },
  items: { kind: '', status: '' },
  activity: { af: '', at: '', ak: '', ap: 0 },
};
const REVIEW_PANEL_ID = 'virtual-account-review';

type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; detail: VirtualAccountDetail }
  | { status: 'notFound' }
  /** The owner is locked (`403 VIRTUAL_ACCOUNT_LOCKED`, MM-D-116): listed, contents refused. */
  | { status: 'locked' }
  | { status: 'error'; error: unknown };

type DialogState =
  | { kind: 'rename' }
  | { kind: 'sharing' }
  | { kind: 'sharedWithYou' }
  | { kind: 'download'; download: DownloadKind }
  | { kind: 'subscribe' }
  | { kind: 'archivedEnvelopes' }
  | { kind: 'moveMoney'; envelopeId?: number }
  | { kind: 'splitUnassigned' }
  | { kind: 'splitRules' }
  | { kind: 'unassignedRegister' }
  | { kind: 'envelopeForm'; envelope?: Envelope }
  | { kind: 'envelopeArchive'; envelope: Envelope }
  | { kind: 'envelopeMove'; envelope: Envelope }
  | { kind: 'envelopeAnalyze'; envelope: Envelope }
  | { kind: 'envelopeAlerts'; envelope: Envelope }
  | null;

function VirtualAccountDetailView({ virtualAccountId }: { virtualAccountId: number }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const refetchMe = useMeRefetch();
  const { reload: reloadList, items: listItems, status: listStatus } = useVirtualAccountsList();
  const navigate = useNavigate();
  const [{ tab: requestedTab, envelope: envelopeParam }, setUrl] = useUrlState(URL_SCHEMA);
  const [state, setState] = useState<DetailState>({ status: 'loading' });
  const [dialog, setDialog] = useState<DialogState>(null);
  const [archiving, setArchiving] = useState(false);
  // Bumped after a move or a split so the ledger refetches what changed under it.
  const [ledgerRefresh, setLedgerRefresh] = useState(0);
  const notify = useNotify();
  // StrictMode double-invokes the mount effect; the detail request is issued once per id.
  const started = useRef(false);
  const reorderTicket = useRef(0);

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', detail: await virtualAccountsApi.get(virtualAccountId) });
    } catch (error) {
      const next: DetailState =
        isApiError(error) && isNotFound(error)
          ? { status: 'notFound' }
          : standingRefusal(error) === 'locked'
            ? { status: 'locked' }
            : { status: 'error', error };
      // A failed re-fetch after a mutation keeps the last good detail on screen — except a LOCKED
      // owner, whose contents are refused from now on.
      setState((prev) => (prev.status === 'ready' && next.status !== 'locked' ? prev : next));
    }
  }, [virtualAccountId]);

  const detail = state.status === 'ready' ? state.detail : null;
  // The caller's served standing (TMMAPP-0110): `/me`, else this detail (an archived one isn't listed).
  const role = useVirtualAccountRole(virtualAccountId, detail);
  // A LOCKED owner's virtual account is listed but its contents are refused: no detail read.
  const lockedByOwner = role.ownerAccess === 'LOCKED';
  // The well's add card (pattern 5): a non-subscriber's press opens purchase, never a create — except
  // a Manager's, which the served standing opens at any tier (MM-D-115).
  const addEnvelope = useSubscriberAction(
    () => setDialog({ kind: 'envelopeForm' }),
    role.isManager && role.canWrite,
  );

  useEffect(() => {
    if (started.current || lockedByOwner) return;
    started.current = true;
    void load();
  }, [load, lockedByOwner]);
  // The standing agreement (the served summary), when there is one; never a read for a LOCKED owner.
  const agreement = useStandingAgreement(lockedByOwner ? null : virtualAccountId);
  // Items to review (TMMAPP-0159 §F 30): the owner's suggested envelopes, a subscriber's own and
  // created here; nobody else has a review tab. The count is their served list.
  const reviews =
    me.tier === 'SUBSCRIBER' &&
    detail !== null &&
    role.isOwner &&
    detail.status !== 'ARCHIVED' &&
    !lockedByOwner;
  const envelopeProposals = useGenesisProposals('envelopes', reviews);
  const reviewCount: ReviewCount =
    envelopeProposals.status === 'ready'
      ? envelopeProposals.proposals.length
      : envelopeProposals.status === 'error'
        ? 'unavailable'
        : 'loading';
  const tab = useReviewTab<VirtualAccountTab>(requestedTab, reviewCount, 'ledger', reviews);

  const listed = me.virtualAccounts.find((entry) => entry.id === virtualAccountId) ?? null;
  const listRow = listItems.find((entry) => entry.id === virtualAccountId) ?? null;
  const locked = lockedByOwner || state.status === 'locked';
  // The title is null (a skeleton) only while the detail loads; a failure keeps the page's name.
  const title = usePageTitle({
    title:
      detail?.name ??
      (locked ? listed?.name : null) ??
      (state.status === 'notFound'
        ? t('notFound.title')
        : state.status === 'error'
          ? t('pageHeader.fallback.virtualAccount')
          : null),
    fallback: t('pageHeader.fallback.virtualAccount'),
  });

  const closeDialog = () => setDialog(null);
  const subscribe = () => setDialog({ kind: 'subscribe' });
  const failWith = (error: unknown) => {
    // A role, grace or locked refusal: the session refetches the role flags and says why.
    if (standingRefusal(error) !== null) void load();
    else if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) subscribe();
    else if (isApiError(error) && error.code === ErrorCodes.CONCURRENT_UPDATE) {
      // An envelope is being moved right now (TMMAPI-0150): the shared copy says so; refetch what changed.
      notify(errorCopy(t, error, 'virtualAccount.errorGeneric'), { severity: 'error' });
      void load();
    } else notify(t('virtualAccount.errorGeneric'), { severity: 'error' });
  };
  const afterMove = async () => {
    await load();
    setLedgerRefresh((n) => n + 1);
  };

  async function handleArchive(d: VirtualAccountDetail) {
    if (archiving) return;
    // Archive is immediate (audit 06 § A2): the page jumps to the next active virtual account;
    // the last active one is refused with v2's copy.
    const next = me.virtualAccounts.find((entry) => entry.mine && entry.id !== d.id);
    if (next === undefined) {
      notify(t('virtualAccount.errorLastActive'), { severity: 'error' });
      return;
    }
    setArchiving(true);
    try {
      await virtualAccountsApi.archive(d.id);
      await Promise.all([refetchMe(), reloadList()]);
      notify(t('virtualAccount.archivedNotice', { name: d.name }));
      void navigate(`/v/${next.id}`);
    } catch (error) {
      const code = isApiError(error) ? error.code : ErrorCodes.UNKNOWN;
      if (code === 'VIRTUAL_ACCOUNT_LAST_ACTIVE') {
        notify(t('virtualAccount.errorLastActive'), { severity: 'error' });
      } else failWith(error);
    } finally {
      setArchiving(false);
    }
  }

  async function leaveTo(notice: string) {
    await Promise.all([refetchMe(), reloadList()]);
    notify(t(notice));
    void navigate('/me/virtual-accounts');
  }

  async function handleDownloadEnvelope(envelope: Envelope) {
    try {
      const { blob, filename } = await envelopesApi.exportXlsx(envelope.id);
      downloadBlob(blob, filename ?? `${slug(envelope.name, 'envelope')}.xlsx`);
      notify(t('virtualAccount.downloadStarted'));
    } catch {
      notify(t('virtualAccount.errorGeneric'), { severity: 'error' });
    }
  }

  async function handleRestore(d: VirtualAccountDetail) {
    try {
      await virtualAccountsApi.restore(d.id);
      await Promise.all([load(), reloadList(), refetchMe()]);
    } catch (error) {
      if (isApiError(error) && error.code === 'VIRTUAL_ACCOUNT_NAME_TAKEN') {
        notify(t('virtualAccounts.errorRestoreNameTaken'), { severity: 'error' });
      } else {
        failWith(error);
      }
    }
  }

  async function handleReorderEnvelopes(ids: Array<string | number>) {
    if (state.status !== 'ready') return;
    const ticket = ++reorderTicket.current;
    const previous = state.detail;
    const nextIds = ids.map(Number);
    setState({
      status: 'ready',
      detail: { ...previous, envelopes: withActiveSortOrder(previous.envelopes, nextIds) },
    });
    try {
      await envelopesApi.reorder(previous.id, nextIds);
      if (ticket !== reorderTicket.current) return;
      await load();
    } catch (error) {
      if (ticket !== reorderTicket.current) return;
      setState({ status: 'ready', detail: previous });
      if (isApiError(error) && error.code === ErrorCodes.REORDER_SET_MISMATCH) {
        notify(t('rules.reorderMismatch'), { severity: 'error' });
        await load();
      } else if (isApiError(error) && error.code === ErrorCodes.USER_FROZEN) {
        notify(t('errors.userFrozen'), { severity: 'error' });
      } else if (isApiError(error) && isNotFound(error)) {
        setState({ status: 'notFound' });
      } else if (standingRefusal(error) !== null) {
        await load();
      } else if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        subscribe();
      } else {
        notify(t('virtualAccount.errorGeneric'), { severity: 'error' });
      }
      throw error;
    }
  }

  async function handleMoved(
    target: MeVirtualAccount,
    audienceAfter: string[],
    envelope: Envelope,
  ) {
    await Promise.all([load(), refetchMe()]);
    const suffix =
      audienceAfter.length > 0
        ? t('envelope.movedVisibleTo', {
            people: new Intl.ListFormat(locale, { type: 'conjunction' }).format(audienceAfter),
          })
        : t('envelope.movedPrivate');
    notify(
      `${t('envelope.moved', { name: envelope.name, virtualAccount: target.name })} ${suffix}`,
    );
  }

  // Every state renders the same header first, so the h1 survives loading → ready and a title that
  // resolves is announced once (TMMAPP-0081). The D1 banners follow it.
  if (locked) {
    return (
      <>
        <PageHeader title={title} />
        <VirtualAccountLockedBody
          owner={
            sponsorName(me, virtualAccountId) || personName(listed?.ownerDisplayName ?? null, t)
          }
        />
      </>
    );
  }

  if (state.status === 'notFound') {
    return (
      <>
        <PageHeader title={title} />
        <NotFoundBody />
      </>
    );
  }

  if (state.status === 'loading') {
    return (
      <>
        <PageHeader title={title} />
        <Paper sx={{ p: '20px 22px', borderTop: `3px solid ${tokens.brandBoundary}` }}>
          <Skeleton width={220} height={48} />
          <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
        </Paper>
      </>
    );
  }

  if (state.status === 'error') {
    return (
      <>
        <PageHeader title={title} />
        <ErrorState
          error={state.error}
          what={t('errors.what.virtualAccount')}
          onRetry={() => {
            setState({ status: 'loading' });
            void load();
          }}
        />
      </>
    );
  }

  const d = state.detail;
  const owner = role.isOwner;
  const archivedVirtualAccount = d.status === 'ARCHIVED';
  // Writers (TMMAPP-0110): the served `canManage` — the owner or a Manager, the virtual account and
  // its owner ACTIVE. Owner-only stays the owner's: Sharing, archive / restore, envelope move, AI.
  const canWrite = role.canWrite && !archivedVirtualAccount;
  const ownerActions = owner && !archivedVirtualAccount;
  const activeEnvelopes = orderEnvelopes(
    d.envelopes.filter((envelope) => envelope.archivedAt === null),
  );
  const archivedCount = d.envelopes.filter((envelope) => envelope.archivedAt !== null).length;
  // Move needs another owned ACTIVE virtual account to move to (`/me` lists the ACTIVE ones).
  const hasMoveTarget = me.virtualAccounts.some((entry) => entry.mine && entry.id !== d.id);
  const sponsor = sponsorName(me, d.id) || personName(d.ownerDisplayName, t);
  const showTab = (next: VirtualAccountTab | ReviewTabValue, patch: UrlPatch = {}) =>
    setUrl({ ...(tab === null ? {} : TAB_PARAMS[tab]), ...patch, tab: next }, { push: true });

  // The v2 envelope card (TMMAPP-0151 §F 18). Every reader gets the kebab (0108 review): a Viewer's
  // holds the read actions only.
  const renderCard = (envelope: Envelope, reorder: ReorderHandleProps | null) => {
    const actions: EnvelopeCardActions = {
      ...(canWrite
        ? {
            writer: {
              onEdit: () => setDialog({ kind: 'envelopeForm', envelope }),
              onArchive: () => setDialog({ kind: 'envelopeArchive', envelope }),
              onMoveMoney: () => setDialog({ kind: 'moveMoney', envelopeId: envelope.id }),
              onCategories: () => showTab('categories'),
            },
          }
        : {}),
      ...(ownerActions && hasMoveTarget
        ? { onMove: () => setDialog({ kind: 'envelopeMove', envelope }) }
        : {}),
      onDownload: () => void handleDownloadEnvelope(envelope),
      ...(ownerActions && envelope.aiAnalysisAllowed
        ? { onAnalyze: () => setDialog({ kind: 'envelopeAnalyze', envelope }) }
        : {}),
      ...(canSeeAlerts(me.tier, owner)
        ? { onAlerts: () => setDialog({ kind: 'envelopeAlerts', envelope }) }
        : {}),
    };
    return (
      <EnvelopeTile
        content={envelopeTileContent(envelope, d.currency, locale, t)}
        to={`/v/${d.id}/e/${envelope.id}`}
        currency={d.currency}
        reorder={reorder}
        menu={<EnvelopeActionsMenu envelope={envelope} actions={actions} reorder={reorder} />}
      />
    );
  };

  const readOnlyChip = !canWrite ? (
    <Box
      component="span"
      data-read-only=""
      sx={{
        display: 'inline-block',
        borderRadius: '3px',
        p: '3px 8px',
        fontSize: 11,
        lineHeight: '16px',
        fontWeight: 500,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: tokens.text2,
        // The `hover` layer over an opaque `surface`: `text2` on it is a guaranteed pair.
        backgroundColor: tokens.surface,
        backgroundImage: `linear-gradient(${tokens.hover}, ${tokens.hover})`,
      }}
    >
      {t('virtualAccount.readOnly')}
    </Box>
  ) : null;

  // The three downloads (audit 06 § A2): owner and sharer alike, each through the C2 range dialog.
  const downloads = (pick: (action: () => void) => () => void) =>
    (
      [
        ['transactions', 'virtualAccount.menu.downloadTransactions'],
        ['pnl', 'virtualAccount.menu.downloadPnl'],
        ['tax', 'virtualAccount.menu.downloadTax'],
      ] as const
    ).map(([download, label]) => (
      <MenuItem
        key={`download-${download}`}
        onClick={pick(() => setDialog({ kind: 'download', download }))}
      >
        {t(label)}
      </MenuItem>
    ));

  return (
    <>
      <PageHeader
        title={title}
        back={{ label: t('nav.virtualAccounts'), to: '/me/virtual-accounts' }}
        // No privacy pill under the title (TMMAPP-0161 item 3): who sees it is the visibility line.
        status={readOnlyChip}
        actions={
          <AskAboutThis
            name={t('askAbout.nameAbout', { name: d.name })}
            scope={[{ kind: 'VIRTUAL_ACCOUNT', id: d.id }]}
          />
        }
        headline={{
          label: t('virtualAccount.balance.label'),
          value: (
            <Box data-balance-figure="">
              <MoneyText
                minor={d.balanceMinor}
                currency={d.currency}
                variant="figure"
                component="span"
              />
            </Box>
          ),
        }}
        menu={
          <OverflowMenu label={t('virtualAccount.actions')} title={d.name} variant="page">
            {(close) => {
              const pick = (action: () => void) => () => {
                close();
                action();
              };
              return owner
                ? [
                    canWrite && (
                      <MenuItem key="rename" onClick={pick(() => setDialog({ kind: 'rename' }))}>
                        {t('virtualAccount.menu.editName')}
                      </MenuItem>
                    ),
                    ownerActions && (
                      <MenuItem key="sharing" onClick={pick(() => setDialog({ kind: 'sharing' }))}>
                        {t('virtualAccount.menu.sharing')}
                      </MenuItem>
                    ),
                    ...downloads(pick),
                    canWrite && (
                      <MenuItem
                        key="split"
                        onClick={pick(() => setDialog({ kind: 'splitUnassigned' }))}
                      >
                        {t('virtualAccount.menu.splitUnassigned')}
                      </MenuItem>
                    ),
                    canWrite && (
                      <MenuItem
                        key="splitRules"
                        onClick={pick(() => setDialog({ kind: 'splitRules' }))}
                      >
                        {t('virtualAccount.menu.splitRules')}
                      </MenuItem>
                    ),
                    ownerActions && (
                      <MenuItem
                        key="archive"
                        disabled={archiving}
                        onClick={pick(() => void handleArchive(d))}
                        sx={destructiveItemSx(tokens)}
                      >
                        {t('virtualAccount.archive')}
                      </MenuItem>
                    ),
                  ]
                : // A member: a Manager also renames and splits Unassigned (Overrides 5); Sharing and
                  // archiving stay the owner's, so they aren't offered.
                  [
                    canWrite && (
                      <MenuItem key="rename" onClick={pick(() => setDialog({ kind: 'rename' }))}>
                        {t('virtualAccount.menu.editName')}
                      </MenuItem>
                    ),
                    <MenuItem
                      key="shared"
                      onClick={pick(() => setDialog({ kind: 'sharedWithYou' }))}
                    >
                      {t('virtualAccount.menu.sharedWithYou')}
                    </MenuItem>,
                    ...downloads(pick),
                    canWrite && (
                      <MenuItem
                        key="split"
                        onClick={pick(() => setDialog({ kind: 'splitUnassigned' }))}
                      >
                        {t('virtualAccount.menu.splitUnassigned')}
                      </MenuItem>
                    ),
                    canWrite && (
                      <MenuItem
                        key="splitRules"
                        onClick={pick(() => setDialog({ kind: 'splitRules' }))}
                      >
                        {t('virtualAccount.menu.splitRules')}
                      </MenuItem>
                    ),
                  ];
            }}
          </OverflowMenu>
        }
      />
      <GraceNotice role={role} owner={sponsor} />
      {archivedVirtualAccount && owner && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => void handleRestore(d)}>
              {t('virtualAccounts.restore')}
            </Button>
          }
        >
          {t('virtualAccount.archivedBanner')}
        </Alert>
      )}

      <BalanceCard
        detail={d}
        envelopes={activeEnvelopes}
        archivedCount={archivedCount}
        canWrite={canWrite}
        // The visibility line is the served list row's (TMMAPP-0154 item 14, spec §0): nothing while
        // the list loads. One the list doesn't carry (an archived one) reads its own details' shares
        // (TMMAPP-0162 item 3).
        visibility={
          listRow !== null
            ? visibilityLine(listRow, t, locale)
            : listStatus === 'loading'
              ? null
              : visibilityLine(audienceFromDetail(d, t), t, locale)
        }
        agreement={agreement}
        renderCard={renderCard}
        onReorder={handleReorderEnvelopes}
        addCard={
          canWrite ? (
            <AddNewCard
              label={t('envelope.addNew')}
              hint={t('envelope.addNewHint')}
              minHeight={220}
              onClick={addEnvelope.run}
            />
          ) : null
        }
        onShowUnassigned={() => showTab('ledger', { filter: 'unassigned', q: '' })}
        onMoveMoney={() => setDialog({ kind: 'moveMoney' })}
        onSplitUnassigned={() => setDialog({ kind: 'splitUnassigned' })}
        onUnassignedRegister={() => setDialog({ kind: 'unassignedRegister' })}
        onOpenArchived={() => setDialog({ kind: 'archivedEnvelopes' })}
      />

      <Box>
        <ReviewTabs
          value={tab}
          count={reviewCount}
          enabled={reviews}
          tabs={[
            { value: 'ledger', label: t('virtualAccount.tab.ledger') },
            { value: 'categories', label: t('virtualAccount.tab.categories') },
            { value: 'forecast', label: t('envelope.tabCashflow') },
            { value: 'meetings', label: t('virtualAccount.tab.meetings') },
            { value: 'items', label: t('virtualAccountItems.tab') },
            { value: 'activity', label: t('audit.tabActivity') },
          ]}
          onChange={(next) => showTab(next)}
          label={t('virtualAccount.tab.label')}
          panelId={REVIEW_PANEL_ID}
          sx={{ mb: '-4px' }}
        />
      </Box>

      {tab === null && <ReviewTabLoading />}
      {reviews && (
        <ReviewPanel
          id={REVIEW_PANEL_ID}
          active={tab === 'review'}
          count={reviewCount}
          mainLabel={t('virtualAccount.tab.ledger')}
          onMain={() => showTab('ledger')}
        >
          <SuggestedEnvelopes
            virtualAccountId={virtualAccountId}
            onCreated={() => void load()}
            source={envelopeProposals}
          />
        </ReviewPanel>
      )}

      {tab === 'ledger' && (
        <LedgerTab
          key={ledgerRefresh}
          virtualAccount={d}
          envelopes={activeEnvelopes}
          canWrite={canWrite}
          onNotify={notify}
          onChanged={load}
          onSubscriptionRequired={subscribe}
        />
      )}
      {tab === 'categories' && (
        <CategoriesTab
          virtualAccount={d}
          envelopes={activeEnvelopes}
          canWrite={canWrite}
          onNotify={notify}
          onShowUncategorized={() => showTab('ledger', { filter: 'uncategorized', q: '' })}
          onSubscriptionRequired={subscribe}
        />
      )}
      {tab === 'forecast' && (
        <VirtualAccountForecastTab
          virtualAccount={d}
          envelopes={activeEnvelopes}
          owner={owner}
          canWrite={canWrite}
          envelopeId={envelopeParam}
          onEnvelopeChange={(id) => setUrl({ envelope: id })}
          onNotify={notify}
        />
      )}
      {tab === 'meetings' && (
        <MeetingsSummariesTab virtualAccountId={d.id} canWrite={canWrite} onNotify={notify} />
      )}
      {tab === 'items' && <VirtualAccountItemsTab virtualAccountId={d.id} />}
      {tab === 'activity' && <ActivityTab virtualAccount={d} />}

      {dialog?.kind === 'rename' && (
        <RenameVirtualAccountDialog
          virtualAccount={d}
          onClose={closeDialog}
          onRenamed={async () => {
            await Promise.all([load(), reloadList(), refetchMe()]);
          }}
        />
      )}
      {dialog?.kind === 'sharing' && (
        <SharingDialog
          detail={d}
          readOnly={archivedVirtualAccount}
          // Closing refreshes the page, the shell's list and /me, so the visibility line follows
          // (TMMAPP-0158 item 5).
          onClose={() => {
            closeDialog();
            void Promise.all([load(), reloadList(), refetchMe()]);
          }}
          // Who sees it is served on the list row and in /me (the chips, the visibility line).
          onChanged={async () => {
            await Promise.all([load(), reloadList(), refetchMe()]);
          }}
          onNotify={notify}
          onLeft={() => void leaveTo('virtualAccount.left')}
        />
      )}
      {dialog?.kind === 'sharedWithYou' && (
        <InfoDialog
          title={d.name}
          body={t('virtualAccount.sharedWithYouBody', { owner: sponsor })}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'download' && (
        <DownloadRangeDialog
          kind={dialog.download}
          virtualAccount={d}
          onClose={closeDialog}
          onNotify={notify}
        />
      )}
      {dialog?.kind === 'archivedEnvelopes' && (
        <ArchivedEnvelopesDialog
          virtualAccount={d}
          canWrite={canWrite}
          onClose={closeDialog}
          onRestored={async (envelope) => {
            notify(
              t('virtualAccount.archivedEnvelopes.restored', {
                name: envelope.name,
                virtualAccount: d.name,
              }),
            );
            await load();
          }}
          onSubscriptionRequired={subscribe}
        />
      )}
      {dialog?.kind === 'moveMoney' && (
        <MoveMoneyDialog
          virtualAccount={d}
          envelopes={activeEnvelopes}
          {...(dialog.envelopeId !== undefined ? { initialTo: dialog.envelopeId } : {})}
          onClose={closeDialog}
          onMoved={async () => {
            notify(t('virtualAccount.moveMoney.done'));
            await afterMove();
          }}
          onSubscriptionRequired={subscribe}
        />
      )}
      {dialog?.kind === 'splitRules' && (
        <SplitRulesDialog
          virtualAccount={d}
          envelopes={activeEnvelopes}
          onClose={closeDialog}
          onNotify={notify}
        />
      )}
      {dialog?.kind === 'splitUnassigned' && (
        <SplitUnassignedDialog
          virtualAccount={d}
          envelopes={activeEnvelopes}
          onClose={closeDialog}
          onSplit={async () => {
            notify(t('virtualAccount.splitUnassigned.done'));
            await afterMove();
          }}
          onSubscriptionRequired={subscribe}
        />
      )}
      {dialog?.kind === 'unassignedRegister' && (
        <UnassignedRegisterDialog virtualAccount={d} onClose={closeDialog} />
      )}
      {dialog?.kind === 'envelopeArchive' && (
        <ConfirmDialog
          title={t('envelope.archiveTitle', { name: dialog.envelope.name })}
          consequence={t('envelope.archiveBody', { name: dialog.envelope.name })}
          confirmLabel={t('envelope.archive')}
          onCancel={closeDialog}
          onConfirm={() => {
            const target = dialog.envelope;
            closeDialog();
            void envelopesApi.archive(target.id).then(load).catch(failWith);
          }}
        />
      )}
      {dialog?.kind === 'envelopeAnalyze' && (
        <AnalysisDialog envelopeId={dialog.envelope.id} onClose={closeDialog} />
      )}
      {dialog?.kind === 'envelopeAlerts' && (
        <AlertsDialog
          envelopeId={dialog.envelope.id}
          envelopeName={dialog.envelope.name}
          currency={d.currency}
          writable={canWriteAlerts(me.tier)}
          onClose={closeDialog}
          onNotify={notify}
        />
      )}
      {dialog?.kind === 'envelopeForm' && (
        <EnvelopeFormDialog
          owner={owner}
          key={dialog.envelope?.id ?? 'new'}
          virtualAccountId={d.id}
          currency={d.currency}
          {...(dialog.envelope ? { envelope: dialog.envelope } : {})}
          nextSortOrder={nextSortOrder(d.envelopes)}
          onClose={closeDialog}
          onSaved={async () => {
            await load();
            notify(t('envelope.saved'));
          }}
        />
      )}
      {dialog?.kind === 'envelopeMove' && (
        <MoveEnvelopeDialog
          envelope={dialog.envelope}
          current={d}
          onClose={closeDialog}
          onMoved={(target, audienceAfter) => handleMoved(target, audienceAfter, dialog.envelope)}
        />
      )}
      {dialog?.kind === 'subscribe' && (
        <Dialog open onClose={closeDialog} maxWidth="xs" aria-label={t('subscribe.cardTitle')}>
          <DialogContent>
            <SubscribeCard />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function VirtualAccountPage() {
  const virtualAccountId = parseRouteId(useParams().virtualAccountId);
  if (virtualAccountId === null) return <RecordNotFound />;
  // Keyed per id so a sidebar hop between virtual accounts starts from a fresh load.
  return <VirtualAccountDetailView key={virtualAccountId} virtualAccountId={virtualAccountId} />;
}
